import { HttpClient, HttpContext, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { handleError } from '../../shared/functions/handle-errors';
import { SKIP_LOADER } from '../../shared/interceptors/loading.interceptor';
import { AdvanceSearchDto, AdvanceSearchRequest } from '../models/advance-search-model';
import { ProviderAddress } from '../models/provider-adddress';
import { ExportTripRequestsToExcelConsumerDashboard, TripAddressDto, TripLegsDto, TripRequestAddressCategoryModel, TripRequestDto, TripRequestsByTypeModel, VerificationDocuments } from '../models/trip-request-models';
import { StartReviewRequest, TripRequest, TripRequestComment, TripRequestConsumerComment } from '../models/trip-request-review-model';

@Injectable({
  providedIn: 'root',
})
export class TripRequestService {
  private http = inject(HttpClient);
  private tripRequestApiUrl = `${environment.apiUrl}TripRequest/`;

   exportTripSummaryToPdf(tripRequestId:number): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`${this.tripRequestApiUrl}ExportTripSummaryToPdf/${tripRequestId}`, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(catchError(handleError));
  }

  exportConsumerRequestToExcel(requst: ExportTripRequestsToExcelConsumerDashboard | null): Observable<HttpResponse<Blob>> {
    return this.http
      .post(`${this.tripRequestApiUrl}ExportConsumerRequestToExcel`, requst ?? {}, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(catchError(handleError));
  }

  exportAdvanceSearchToExcel(requst: AdvanceSearchRequest | null): Observable<HttpResponse<Blob>> {
    return this.http
      .post(`${this.tripRequestApiUrl}ExportAdvanceSearchToExcel`, requst ?? {}, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(catchError(handleError));
  }

  addTripRequestReviewComment(comment: TripRequestComment): Observable<TripRequest> {
    return this.http.post<TripRequest>(`${this.tripRequestApiUrl}AddTripRequestReviewComment`, comment).pipe(catchError(handleError));
  }

    addTripRequestConsumerComment(commentRequest: TripRequestConsumerComment): Observable<TripRequestConsumerComment[]> {
        return this.http.post<TripRequestConsumerComment[]>(`${this.tripRequestApiUrl}AddTripRequestConsumerComment`, commentRequest).pipe(catchError(handleError));
    }

  startTripRequestReview(request: StartReviewRequest): Observable<TripRequest>  {
    return this.http.post<TripRequest>(`${this.tripRequestApiUrl}StartTripRequestReview`, request).pipe(catchError(handleError));
  }

  submitTripRequestReview(formData: FormData): Observable<TripRequest> {
    return this.http.post<TripRequest>(`${this.tripRequestApiUrl}SubmitTripRequestReview`, formData).pipe(catchError(handleError));
  }

  startReviewTripRequest(tripRequestId: number): Observable<TripRequest> {
    return this.http.post<TripRequest>(`${this.tripRequestApiUrl}StartReviewTripRequest`, tripRequestId).pipe(catchError(handleError));
  }

  getTripRequestsForReview(tripRequestId: number): Observable<TripRequest> {
    return this.http.get<TripRequest>(`${this.tripRequestApiUrl}GetTripRequestsForReview/${tripRequestId}`).pipe(catchError(handleError));
  }

  getTripRequestsByRecipientId(recipientId: number, fromDate?: Date | null, toDate?: Date | null): Observable<TripRequestsByTypeModel> {
    let params = new HttpParams();
    if (fromDate) {
      params = params.set('fromDate', fromDate.toISOString());
    }
    if (toDate) {
      params = params.set('toDate', toDate.toISOString());
    }

    return this.http.get<TripRequestsByTypeModel>(`${this.tripRequestApiUrl}GetTripRequestsByRecipientId/${recipientId}`, { params }).pipe(catchError(handleError));
  }

  CancelTripRequest(requestId: number): Observable<void> {
    return this.http.post<void>(`${this.tripRequestApiUrl}CancelTripRequest/${requestId}`, {}).pipe(catchError(handleError));
  }

  getTripAndSavedAddresses(recipientId: number, countyCode: string, category: string): Observable<ProviderAddress[]> {
    return this.http.get<ProviderAddress[]>(`${this.tripRequestApiUrl}GetTripAndSavedAddresses/${recipientId}/${countyCode}/${category}`).pipe(catchError(handleError));
  }

  getTripRequestAddressCategories(recipientId: number): Observable<TripRequestAddressCategoryModel[]> {
    return this.http.get<TripRequestAddressCategoryModel[]>(`${this.tripRequestApiUrl}GetTripRequestAddressCategories/${recipientId}`).pipe(catchError(handleError));
  }

  saveAppointments(tripRequest: TripRequestDto): Observable<TripRequestDto> {
    return this.http.post<TripRequestDto>(`${this.tripRequestApiUrl}SaveAppointments`, tripRequest).pipe(catchError(handleError));
  }

  getTripAppointments(tripRequestId: number, type: string): Observable<TripRequestDto> {
    return this.http.get<TripRequestDto>(`${this.tripRequestApiUrl}GetTripAppointments/${tripRequestId}/${type}`).pipe(catchError(handleError));
  }

  getPostTripRequest(tripRequestId: number): Observable<TripRequestDto> {
    return this.http.get<TripRequestDto>(`${this.tripRequestApiUrl}GetPostTripRequest/${tripRequestId}`).pipe(catchError(handleError));
  }

  getPreTripRequest(tripRequestId: number): Observable<TripRequestDto> {
    return this.http.get<TripRequestDto>(`${this.tripRequestApiUrl}GetPreTripRequest/${tripRequestId}`).pipe(catchError(handleError));
  }

  saveDriverInformation(tripRequest: TripRequestDto): Observable<TripRequestDto> {
    return this.http.post<TripRequestDto>(`${this.tripRequestApiUrl}SaveTripDriver`, tripRequest).pipe(catchError(handleError));
  }

  saveTripLocation(tripRequest: TripRequestDto): Observable<TripRequestDto> {
    return this.http.post<TripRequestDto>(`${this.tripRequestApiUrl}SaveTripLocation`, tripRequest).pipe(catchError(handleError));
  }

  saveTripPassenger(tripRequest: TripRequestDto): Observable<TripRequestDto> {
    return this.http.post<TripRequestDto>(`${this.tripRequestApiUrl}SaveTripPassenger`, tripRequest).pipe(catchError(handleError));
  }

  saveTripDocuments(formData: FormData): Observable<TripRequestDto> {
    return this.http.post<TripRequestDto>(`${this.tripRequestApiUrl}SaveTripDocuments`, formData).pipe(catchError(handleError));
  }

  saveAgentTripRequest(formData: FormData): Observable<string> {
    return this.http.post(`${this.tripRequestApiUrl}SaveAgentTripRequest`, formData, {responseType: 'text'}).pipe(catchError(handleError));
  }

  saveTripSummary(tripRequest: TripRequestDto): Observable<string> {
    return this.http.post(`${this.tripRequestApiUrl}SaveTripSummary`, tripRequest, { responseType: 'text' }).pipe(catchError(handleError));
  }

  getPreTripFlag(recipientId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.tripRequestApiUrl}GetPreTripFlag/${recipientId}`).pipe(catchError(handleError));
  }

  searchTrips(request: AdvanceSearchRequest): Observable<AdvanceSearchDto[]> {
    return this.http.post<AdvanceSearchDto[]>(`${this.tripRequestApiUrl}SearchTrips`, request).pipe(catchError(handleError));
  }

  getTripLegs(addresses: TripAddressDto[], isRoundTrip: boolean ): Observable<TripLegsDto[]> {
    return this.http.post<TripLegsDto[]>(`${this.tripRequestApiUrl}GetTripLegs/${isRoundTrip}`, addresses).pipe(catchError(handleError));
  }

  getTripRate() : Observable<number> {
     return this.http.get<number>(`${this.tripRequestApiUrl}GetTripRate`).pipe(catchError(handleError));
  }

  getVerificationDocuments(recipientId: number, appointmentTypeId: number) : Observable<VerificationDocuments[]> {
     return this.http.get<VerificationDocuments[]>(`${this.tripRequestApiUrl}GetVerificationDocuments/${recipientId}/${appointmentTypeId}`).pipe(catchError(handleError));
  }

  downloadVerificationFile(): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`${this.tripRequestApiUrl}downloadVerificationFile`, {
        observe: 'response',
        responseType: 'blob',
        context: new HttpContext().set(SKIP_LOADER, true)
      })
      .pipe(catchError(handleError));
  }
}
